import * as Sentry from '@sentry/react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ETAPaymentMethod } from '~/api/ETA';
import { PaymentSystem } from '~/api/Order';
import { company } from '~/company/Company';
import type { AwsPaymentRef } from '~/components/AwsPayment/AwsPayment';
import { ModalType } from '~/components/Modal/interface';
import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import adyenService from '~/services/Adyen';
import { notificationService } from '~/services/NotificationService';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import { awsPaymentStore } from '~/stores/AwsPaymentStore';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import { formatPriceWithCurrency } from '~/utils/formaters';
import { clearStoredPromocode, getStoredPromocode } from '~/utils/referralLink';

import { MODAL_EDIT_ADDRESS_PARAM, useModal } from './useModal';
import { useVibration } from './useVibration';

interface CheckoutButtonState {
  isDisabled: boolean;
  onClick: () => void;
  text: string;
  description: string;
  isDisabledForm: () => boolean;
  isLoading?: boolean;
}

export const useCheckoutButton = (): CheckoutButtonState => {
  const awsPaymentRef = useRef<AwsPaymentRef>(null);
  const { t } = useTranslation();
  const { openModal } = useModal();
  const stripe = useStripe();
  const elements = useElements();
  const { vibrate } = useVibration({ type: 'impactMedium' });

  const slotNotSelected = checkoutStore.deliveries?.some(
    (delivery) => delivery.slotDeliveryDetails?.isAutomatic,
  );

  const isRecieverData =
    checkoutStore.nameVal?.trim().length > 0 &&
    checkoutStore.emailVal?.trim().length > 0 &&
    checkoutStore.isEmailValid;

  const handleOpenRecieverData = () => {
    openModal(ModalType.CheckoutAddRecieverData, 'checkout');
  };

  const handleOpenEditAddress = () => {
    deliveryAddressStore.setEditAddressData({
      addressId: userStore.deliveryAddress?.addressId || '',
      address2: userStore.deliveryAddress?.address2 || '',
      addressFloor: userStore.deliveryAddress?.addressFloor || '',
      addressBld: userStore.deliveryAddress?.addressBld || '',
      addressApt: userStore.deliveryAddress?.addressApt || '',
      type: userStore.deliveryAddress?.type || '',
      comment: userStore.deliveryAddress?.comment || '',
      instructions: userStore.deliveryAddress?.instructions || [],
    });
    openModal(ModalType.EditAddressDetails, 'checkout', {
      [MODAL_EDIT_ADDRESS_PARAM]: userStore.deliveryAddress?.addressId || '',
      [REDIRECT_QUERY_NAME]: '',
    });
  };

  const isBuildingMissing = () => {
    const { deliveryAddress } = userStore;
    return (
      !deliveryAddress?.addressBld || deliveryAddress.addressBld.trim() === ''
    );
  };

  const handleOpenFirstUnselectedSlot = () => {
    const firstUnselectedIndex = checkoutStore.deliveries?.findIndex(
      (delivery) => delivery.slotDeliveryDetails?.isAutomatic,
    );

    if (firstUnselectedIndex !== undefined && firstUnselectedIndex >= 0) {
      const delivery = checkoutStore.deliveries?.[firstUnselectedIndex];
      const order = cartStore.deliveriesOrders[firstUnselectedIndex];

      if (delivery && order) {
        checkoutStore.slotSelectionModalStore.setSelectedSlot(delivery, order);
        openModal(ModalType.SelectDateCD);
      }
    }
  };

  const handleFocusCardInput = () => {
    if (checkoutStore.activePaymentSystem === PaymentSystem.adyen) {
      const cardInputContainer = document.getElementById(
        'adyen-dropin-container',
      );
      if (cardInputContainer) {
        cardInputContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    } else if (checkoutStore.activePaymentSystem === PaymentSystem.stripe) {
      const cardInputContainer = document.getElementById(
        'stripe-card-input-container',
      );
      if (cardInputContainer) {
        cardInputContainer.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  };

  const handleSubmit = () => {
    const pay = () => {
      checkoutStore.orderCheckout(stripe, elements).then(() => {
        const storedPromocode = getStoredPromocode();
        if (
          cartStore.promocode.success &&
          cartStore.promocode.value === storedPromocode?.code
        ) {
          clearStoredPromocode();
        }
      });
    };
    Sentry.withScope((scope) => {
      scope.setTag('payment_flow', checkoutStore.activePaymentSystem);
      scope.setContext('payment_details', {
        payment_method: checkoutStore.activePaymentMethod,
        amount: cartStore.finalPrice,
        currency: orderStore.currency,
        is_mobile: mainStore.isRN,
      });
      Sentry.addBreadcrumb({
        category: 'payment',
        message: 'Payment flow initiated',
        level: 'info',
      });
    });

    vibrate();

    if (company.useAwsPaymentMethod && awsPaymentRef.current) {
      awsPaymentRef.current.handleSubmitForm();
      return;
    }

    if (checkoutStore.activePaymentSystem === PaymentSystem.adyen) {
      Sentry.addBreadcrumb({
        category: 'payment',
        message: 'Adyen payment Starting checkout',
        level: 'info',
      });

      Sentry.addBreadcrumb({
        category: 'payment',
        message: 'Adyen payment Submitting payment',
        level: 'info',
      });

      if (
        (checkoutStore.isAddNewCard || !checkoutStore.paymentsMethodId) &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
        checkoutStore.activePaymentMethod !== MobilePaymentMethod.ApplePay &&
        checkoutStore.activePaymentMethod !== MobilePaymentMethod.GooglePay
      ) {
        adyenService.submit();
        return;
      }

      pay();
      return;
    }

    if (checkoutStore.activePaymentSystem === PaymentSystem.stripe) {
      pay();
      return;
    }

    if (checkoutStore.activePaymentSystem === PaymentSystem.octo) {
      pay();
      return;
    }

    if (checkoutStore.activePaymentSystem === PaymentSystem.pvit) {
      if (checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR) {
        if (!checkoutStore.mobileMoneyPhoneVal.trim().length) {
          notificationService.error(t('mobileMoneyPhoneInvalid'));
          return;
        }
      }
      pay();
      return;
    }

    if (
      checkoutStore.activePaymentMethod === MobilePaymentMethod.GooglePay ||
      checkoutStore.activePaymentMethod === MobilePaymentMethod.ApplePay
    ) {
      pay();
      return;
    }

    if (checkoutStore.activePaymentMethod === ETAPaymentMethod.Cash) {
      pay();
    }
  };

  const isDisabledForm = (): boolean => {
    if (
      cartStore.calculationProcess.isLoading ||
      checkoutStore.slotSelectionModalStore.isLoading ||
      cartStore.calculationProcess.isError
    ) {
      return true;
    }
    if (checkoutStore.isLoading) {
      return true;
    }

    if (awsPaymentStore.isIframeLoading) {
      return true;
    }

    if (
      checkoutStore.activePaymentSystem === PaymentSystem.octo &&
      checkoutStore.orderId
    ) {
      return true;
    }

    if (orderStore.isETALoading) {
      return true;
    }

    if (
      checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
      checkoutStore.activePaymentSystem === PaymentSystem.stripe &&
      (stripe === null || elements === null)
    ) {
      return true;
    }

    if (
      checkoutStore.selectedDeliveryDate &&
      checkoutStore.selectedDeliveryDate !== '0' &&
      checkoutStore.selectedDeliverySlot
    ) {
      const selectedDate =
        checkoutStore.deliverySlots[checkoutStore.selectedDeliveryDate];

      if (!selectedDate) {
        throw new Error('Something went wrong');
      }

      const slot = selectedDate.find(
        ({ schedule_slot_id }) =>
          schedule_slot_id ===
          checkoutStore.selectedDeliverySlot?.schedule_slot_id,
      );
      if (!slot) {
        return true;
      }
    }

    if (
      checkoutStore.activePaymentMethod === ETAPaymentMethod.Card &&
      checkoutStore.activePaymentSystem !== PaymentSystem.myfatoorah &&
      checkoutStore.activePaymentSystem !== PaymentSystem.midtrans &&
      checkoutStore.activePaymentSystem !== PaymentSystem.amazon &&
      checkoutStore.activePaymentSystem !== PaymentSystem.adyen &&
      checkoutStore.activePaymentSystem !== PaymentSystem.verifone &&
      checkoutStore.activePaymentSystem !== PaymentSystem.octo
    ) {
      return checkoutStore.isDisabled;
    }

    return false;
  };

  if (
    cartStore.totalCartPrice.minimal_order_value_error?.length &&
    cartStore.totalCartPrice.minimal_order_value_error[0]
  ) {
    return {
      isDisabled: true,
      onClick: () => void 0,
      text: t('errors:minCartError', {
        amount: formatPriceWithCurrency(
          cartStore.totalCartPrice.minimal_order_value_error[0]
            ?.minimalOrderValue / 100,
        ),
      }),
      description: t('errors:minCartErrorDetails', {
        amount: formatPriceWithCurrency(
          (cartStore.totalCartPrice.minimal_order_value_error[0]
            ?.minimalOrderValue -
            cartStore.totalCartPrice.minimal_order_value_error[0]
              ?.orderPriceWithoutDelivery) /
            100,
        ),
      }),
      isDisabledForm,
    };
  }

  if (isBuildingMissing()) {
    return {
      isDisabled: false,
      onClick: handleOpenEditAddress,
      text: t('checkout:addBuildingNumber'),
      description: t('checkout:tapHereToContinue'),
      isDisabledForm,
    };
  }

  if (!isRecieverData) {
    return {
      isDisabled: false,
      onClick: handleOpenRecieverData,
      text: t('checkout:addRecieverData'),
      description: t('checkout:tapHereToContinue'),
      isDisabledForm,
    };
  }

  if (slotNotSelected) {
    return {
      isDisabled: false,
      onClick: handleOpenFirstUnselectedSlot,
      text: t('checkout:addDeliveryTime'),
      description: t('checkout:tapHereToContinue'),
      isDisabledForm,
    };
  }

  if (
    checkoutStore.activePaymentMethod === ETAPaymentMethod.Card &&
    checkoutStore.activePaymentSystem !== PaymentSystem.octo &&
    (checkoutStore.isAddNewCard || !checkoutStore.payments?.length) &&
    !checkoutStore.isReadyForPayment
  ) {
    return {
      isDisabled: false,
      onClick: handleFocusCardInput,
      text: t('checkout:addPaymentMethod'),
      description: t('checkout:tapHereToContinue'),
      isDisabledForm,
    };
  }

  return {
    isDisabled: isDisabledForm(),
    onClick: handleSubmit,
    text: t('confirmOrder'),
    description: '',
    isDisabledForm,
    isLoading:
      checkoutStore.isLoading ||
      (checkoutStore.activePaymentSystem === PaymentSystem.octo &&
        !!checkoutStore.orderId),
  };
};
